<template>
    <div class="kling-model-page">
        <!-- Video Generator Section -->
        <ModelHeroSection ref="heroRef" :title="t('klingO1.generatorTitle')" :description="generatorDescription"
            model-name="Kling O1" model-key="KLING_O1" :tabs="tabs" redirect-url="/ai-video" @generate="handleGenerate">
            <template #logo>
                <div class="kling-logo">
                    <img src="https://static.weryai.com/growth/image/b472b35b9fb1e029be77cb87d4fb28c3.png"
                        :alt="t('klingO1.logoAlt')" onerror="this.style.display='none';this.parentNode.innerHTML='K'" />
                </div>
            </template>
        </ModelHeroSection>

        <!-- Core Features Section -->
        <CoreFeaturesSection :title="t('klingO1.coreFeaturesTitle')" :features="coreFeatures" />

        <!-- How to Use Section -->
        <HowToUseSection :title="t('klingO1.howToUseTitle')" :steps="howToUseSteps" />

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection v-if="youtubeVideos.length > 0" :title="t('klingO1.youtubeTitle')"
            :videos="youtubeVideos" />

        <!-- X Posts Section -->
        <TwitterPosts v-if="twitterTweetUrls.length > 0" :title="t('klingO1.xPostsTitle')"
            :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <ExploreModelsSection :title="t('klingO1.exploreModelsTitle')" :models="exploreModels" />

        <!-- FAQ Section -->
        <FAQSection :title="t('klingO1.faqTitle')" :faqs="faqData" />

        <!-- CTA Section -->
        <CTASection :title="t('klingO1.ctaTitle')" :description="t('klingO1.ctaDescription')"
            :button-text="t('klingO1.ctaButton')" :disclaimer="t('klingO1.ctaDisclaimer')" @click="scrollToGenerator" />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { useI18n } from '~/composables/useI18n'
import {
    ModelHeroSection,
    CoreFeaturesSection,
    HowToUseSection,
    YouTubeCarouselSection,
    ExploreModelsSection,
    FAQSection,
    CTASection,
} from '~/components/model/video'
import { analyticsEvent } from '~/utils/log-event-func'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

const demoVideos = {
    feature1: 'https://static.weryai.com/cms/kling_O1_video_sample_instance_video1.mp4',
    feature2: 'https://static.weryai.com/cms/kling_O1_video_sample_instance_video2.mp4',
    feature3: 'https://static.weryai.com/cms/kling_O1_video_sample_instance_video3.mp4',
    feature4: 'https://static.weryai.com/cms/kling_O1_video_sample_instance_video4.mp4',
    feature5: 'https://static.weryai.com/cms/kling_O1_video_sample_instance_video5.mp4',
} as const

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'kling_o1' })

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('klingO1.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'kling_o1',
        type: 'video',
    })
})

// Hero Section Ref
const heroRef = ref<InstanceType<typeof ModelHeroSection> | null>(null)

// Scroll to generator
const scrollToGenerator = () => {
    heroRef.value?.scrollToSection()
}

// Generator Description (plain text without highlights)
const generatorDescription = computed(() => {
    return t('klingO1.generatorDescription')
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('klingO1.tabImageToVideo'), external: false },
    { name: t('klingO1.tabTextToVideo'), external: false },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

// Core Features Data
const coreFeatures = computed(() => [
    {
        title: t('klingO1.feature1Title'),
        description: t('klingO1.feature1Description'),
        videoUrl: demoVideos.feature1,
    },
    {
        title: t('klingO1.feature2Title'),
        description: t('klingO1.feature2Description'),
        videoUrl: demoVideos.feature2,
    },
    {
        title: t('klingO1.feature3Title'),
        description: t('klingO1.feature3Description'),
        videoUrl: demoVideos.feature3,
    },
    {
        title: t('klingO1.feature4Title'),
        description: t('klingO1.feature4Description'),
        videoUrl: demoVideos.feature4,
    },
    {
        title: t('klingO1.feature5Title'),
        description: t('klingO1.feature5Description'),
        videoUrl: demoVideos.feature5,
    },
])

// How to Use Steps
const howToUseSteps = computed(() => [
    {
        title: t('klingO1.step1Title'),
        description: t('klingO1.step1Description'),
    },
    {
        title: t('klingO1.step2Title'),
        description: t('klingO1.step2Description'),
    },
    {
        title: t('klingO1.step3Title'),
        description: t('klingO1.step3Description'),
    },
    {
        title: t('klingO1.step4Title'),
        description: t('klingO1.step4Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('klingO1.howToUseTitle')),
    howToUseSteps
)

// YouTube Videos Data
const youtubeVideos = computed(() => [
    {
        title: t('klingO1.youtubeVideo1Title'),
        embedUrl: 'https://www.youtube.com/embed/tO6vnBi5e2M',
    },
    {
        title: t('klingO1.youtubeVideo2Title'),
        embedUrl: 'https://www.youtube.com/embed/GE5OoLI5VJA',
    },
    {
        title: t('klingO1.youtubeVideo3Title'),
        embedUrl: 'https://www.youtube.com/embed/W7eOHALS32g',
    },
    {
        title: t('klingO1.youtubeVideo4Title'),
        embedUrl: 'https://www.youtube.com/embed/AF9NfQehuG0',
    },
    {
        title: t('klingO1.youtubeVideo5Title'),
        embedUrl: 'https://www.youtube.com/embed/kRlbbKZIMFU',
    },
    {
        title: t('klingO1.youtubeVideo6Title'),
        embedUrl: 'https://www.youtube.com/embed/WNzC884lc7c',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// Twitter Tweet URLs Data
const twitterTweetUrls = ref<string[]>([
    'https://twitter.com/Kling_ai/status/1995506929461002590',
    'https://twitter.com/minchoi/status/1862873978605367681',
    'https://twitter.com/FellMentKE/status/1958822390479429936',
    'https://twitter.com/Damn_coder/status/1995535275628601792',
    'https://twitter.com/0xFramer/status/1978842764663279694',
    'https://twitter.com/openart_ai/status/1996963139384918042',
    'https://twitter.com/ai_artworkgen/status/1996935387248296236',
    'https://twitter.com/AngryTomtweets/status/1995619392475091221',
    'https://twitter.com/genel_ai/status/1995674277446832351',
    'https://twitter.com/mhdfaran/status/1998026475493372192',
    'https://twitter.com/AngryTomtweets/status/1995915325485793740',
    'https://twitter.com/ai_for_success/status/1997672465267577183',
    'https://twitter.com/genel_ai/status/1996043945349185899',
])

// Explore Other Models Data
const exploreModels = computed(() => [
    {
        title: 'Seedance',
        icon: 'https://static.weryai.com/growth/image/98653aa87810721ed41e47d1df57b068.png',
        path: '/video/seedance',
    },
    {
        title: 'Runway Gen-4.5',
        icon: 'https://static.weryai.com/growth/image/a65ca4242d4a0dc3f1b65e1d96471144.png',
        path: '/video/runway-ai-4-5',
    },
    {
        title: 'Sora 2',
        icon: 'https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png',
        path: '/video/sora/sora-2',
    },
])

// FAQ Data
const faqData = computed(() => [
    {
        question: t('klingO1.faq1Question'),
        answer: t('klingO1.faq1Answer'),
    },
    {
        question: t('klingO1.faq2Question'),
        answer: t('klingO1.faq2Answer'),
    },
    {
        question: t('klingO1.faq3Question'),
        answer: t('klingO1.faq3Answer'),
    },
    {
        question: t('klingO1.faq4Question'),
        answer: t('klingO1.faq4Answer'),
    },
    {
        question: t('klingO1.faq5Question'),
        answer: t('klingO1.faq5Answer'),
    },
    {
        question: t('klingO1.faq6Question'),
        answer: t('klingO1.faq6Answer'),
    },
])

// SEO Meta Data - Handled by useMultilingualSeo

// Structured Data for SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'SoftwareApplication',
                    name: t('klingO1.structuredDataName'),
                    description: t('klingO1.structuredDataDescription'),
                    applicationCategory: 'MultimediaApplication',
                    operatingSystem: 'Web',
                    offers: {
                        '@type': 'Offer',
                        price: '0',
                        priceCurrency: 'USD',
                    },
                    provider: {
                        '@type': 'Organization',
                        name: '微鲤AI',
                        url: 'https://www.weryai.com.cn',
                    },
                    featureList: [
                        t('klingO1.structuredDataFeature1'),
                        t('klingO1.structuredDataFeature2'),
                        t('klingO1.structuredDataFeature3'),
                        t('klingO1.structuredDataFeature4'),
                        t('klingO1.structuredDataFeature5'),
                    ],
                })
            ),
        },
    ],
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';

.kling-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}
</style>
